import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Button as MuiButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  AlertColor,
  CircularProgress,
} from "@mui/material";
import { Box, spacing } from "@mui/system";
import { Delete, Edit, Search } from "@mui/icons-material";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  openPropertyForm,
  fetchProperties,
  setProperty,
  deleteProperty,
} from "../../../redux/slices/property";
import useAuth from "../../../hooks/useAuth";
import { usePermission } from "../../../hooks/usePermission";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

type LocationState = {
  snackbar?: {
    message: string;
    severity?: AlertColor;
  };
};

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_API_KEY || "";

function PropertiesList(props: any) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();

  const { status = "idle" } = useAppSelector((state) => state.property || {});
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Check permissions using RBAC
  const { hasPermission: canDeleteProperty } = usePermission(
    "property",
    "delete",
    associationId
  );

  useEffect(() => {
    if (associationId) {
      dispatch(fetchProperties(associationId));
    }
  }, [dispatch, associationId]);

  const [sortField, setSortField] = useState<"name" | "code" | "address">(
    "name"
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const handleSort = (field: "name" | "code" | "address") => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortArrow = (field: "name" | "code" | "address") => {
    if (sortField !== field) return "";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const sortedRows = [...(props.rows || [])].sort((a, b) => {
    const aVal = a[sortField]?.toLowerCase?.() || "";
    const bVal = b[sortField]?.toLowerCase?.() || "";
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Kiinteistöt
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={"small"}
          onClick={() => props.handleCreateProperty(undefined, true)}
        >
          Lisää
        </Button>
      </CardContent>

      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={() => handleSort("name")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Nimi{getSortArrow("name")}
              </TableCell>
              <TableCell
                onClick={() => handleSort("code")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Kiinteistötunnus{getSortArrow("code")}
              </TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Ei kiinteistöjä
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: any) => (
                <TableRow key={row.code} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.code}</TableCell>
                  <TableCell>
                    <Search
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleShowProperty(row, true)}
                    />{" "}
                    <Edit
                      style={{ cursor: "pointer" }}
                      onClick={() => props.handleEditUser(row, true)}
                    />{" "}
                    {canDeleteProperty ? (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => props.handleDelete(row)}
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Properties() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { properties, open, editable } = useAppSelector(
    (state) => state.property
  );

  // const handleOpen = () => {
  //   dispatch(openPropertyForm());
  //   // dispatch(editPropertyForm());
  // };

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const location = useLocation();

  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state?.snackbar) {
      setSnackbarMessage(state.snackbar.message);
      setSnackbarSeverity(state.snackbar.severity || "success");
      setOpenSnackbar(true);
      setProgress(100);

      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleShowProperty = (property: any, isOpen: boolean) => {
    dispatch(setProperty(property));
    dispatch(openPropertyForm(isOpen));
  };

  const handleEditProperty = (property: any, isOpen: boolean) => {
    dispatch(setProperty(property));
    //dispatch(openPropertyForm(isOpen));
    navigate(`/properties/${property.id}`);
    // dispatch(editPropertyForm());
  };

  const handleCreateProperty = (property: any, isOpen: boolean) => {
    dispatch(setProperty(property));
    navigate(`/properties/new`);
  };

  const handleDelete = async (property: any) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa "${property.name}"?`
    );
    if (confirmed) {
      setProgress(100);
      try {
        await dispatch(deleteProperty(property.id));
        setSnackbarMessage("Kiinteistö poistettu onnistuneesti!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (e) {
        setSnackbarMessage("Virhe poistettaessa kiinteistöä.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  useEffect(() => {
    dispatch(fetchProperties(associationId));

    return () => {
      dispatch(openPropertyForm(false));
    };
  }, [dispatch, currentUser, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Kiinteistöt" />
      <Typography variant="h3" gutterBottom display="inline">
        Kiinteistöt
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Kiinteistöt</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PropertiesList
        rows={properties}
        editable={editable}
        handleShowProperty={handleShowProperty}
        handleEditUser={handleEditProperty}
        handleDelete={handleDelete}
        handleCreateProperty={handleCreateProperty}
        openForm={open}
      />

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Properties;
